import React from 'react';
import clsx from 'clsx';

type TypographyVariant =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'subtitle3'
    | 'body1'
    | 'body2'
    | 'bodyLarge'
    | 'caption'
    | 'overline';

type TypographyColor =
    | 'primary'
    | 'secondary'
    | 'textPrimary'
    | 'textSecondary'
    | 'error'
    | 'warning'
    | 'success'
    | 'inherit';

type TypographyAlign = 'left' | 'center' | 'right' | 'justify' | 'inherit';

type TypographyComponent = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'label';

interface TypographyProps {
    variant?: TypographyVariant;
    color?: TypographyColor;
    align?: TypographyAlign;
    component?: TypographyComponent;
    className?: string;
    children?: React.ReactNode;
    gutterBottom?: boolean;
    noWrap?: boolean;
    loading?: boolean;
    skeletonWidth?: string | number;
    style?: React.CSSProperties;
}

// Variant to component mapping (default semantic HTML)
const variantMapping: Record<TypographyVariant, TypographyComponent> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    subtitle1: 'h6',
    subtitle2: 'h6',
    subtitle3: 'h6',
    body1: 'p',
    body2: 'p',
    bodyLarge: 'p',
    caption: 'span',
    overline: 'span',
};

// Variant styles
const variantStyles: Record<TypographyVariant, string> = {
    h1: 'text-6xl font-light leading-tight tracking-tight',
    h2: 'text-5xl font-light leading-tight tracking-tight',
    h3: 'text-4xl font-normal leading-tight',
    h4: 'text-3xl font-normal leading-snug',
    h5: 'text-2xl font-normal leading-snug',
    h6: 'text-xl font-medium leading-snug',
    subtitle1: 'text-base font-normal leading-relaxed',
    subtitle2: 'text-base font-medium leading-relaxed',
    subtitle3: 'text-sm font-medium leading-relaxed',
    body1: 'text-base font-normal leading-relaxed',
    body2: 'text-sm font-normal leading-relaxed',
    bodyLarge: 'text-lg font-normal leading-relaxed',
    caption: 'text-xs font-normal leading-normal',
    overline: 'text-xs font-normal uppercase tracking-wider leading-relaxed',
};

// Color styles
const colorStyles: Record<TypographyColor, string> = {
    primary: 'text-gray-900',
    secondary: 'text-gray-600',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-500',
    error: 'text-red-600',
    warning: 'text-orange-500',
    success: 'text-green-600',
    inherit: '',
};

// Align styles
const alignStyles: Record<TypographyAlign, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
    inherit: '',
};

// Skeleton styles for different variants
const skeletonHeights: Record<TypographyVariant, string> = {
    h1: 'h-12',
    h2: 'h-10',
    h3: 'h-9',
    h4: 'h-8',
    h5: 'h-7',
    h6: 'h-6',
    subtitle1: 'h-6',
    subtitle2: 'h-5',
    subtitle3: 'h-5',
    body1: 'h-6',
    body2: 'h-5',
    bodyLarge: 'h-7',
    caption: 'h-4',
    overline: 'h-4',
};

export const Typography: React.FC<TypographyProps> = ({
                                                          variant = 'body1',
                                                          color = 'textPrimary',
                                                          align = 'inherit',
                                                          component,
                                                          className,
                                                          children,
                                                          gutterBottom = false,
                                                          noWrap = false,
                                                          loading = false,
                                                          skeletonWidth = '100%',
                                                          style,
                                                      }) => {

    const Component = component || variantMapping[variant];

    // If loading, render skeleton
    if (loading) {
        const widthStyle = typeof skeletonWidth === 'number'
            ? `${skeletonWidth}px`
            : skeletonWidth;

        return (
            <div
                className={clsx(
                    'skeleton-loader rounded',
                    skeletonHeights[variant],
                    gutterBottom && 'mb-1',
                    className
                )}
                style={{width: widthStyle, ...style}}
            />
        );
    }

    return (
        <Component
            style={style}
            className={clsx(
                variantStyles[variant],
                colorStyles[color],
                alignStyles[align],
                gutterBottom && 'mb-1',
                noWrap && 'truncate',
                className
            )}
        >
            {children}
        </Component>
    );
};

export default Typography;